import { useState, useEffect } from 'react';
import { Button } from '../../Components/UI/button';
import { Input } from '../../Components/UI/input';
import { Label } from '../../Components/UI/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from '../../Components/UI/dialog';
import { toast } from 'sonner';
import { Check, X, ArrowLeft, Trash2, Loader2 } from 'lucide-react';
import { useSelector } from 'react-redux';
import { saveMastersConfigAPI } from '../Settings/MasterDataAPI';
import { AlertBox } from '../../Components/Common/AlertBox';
import useIsMobile from '../../Hooks/useIsMobile';

const ExtraFieldEntryDialog = ({
    open,
    onOpenChange,
    mode = 'add', // 'add' or 'edit'
    type = 'connection', // 'connection' or 'thread'
    initialValue = '',
    fieldID = null,
    threadTypeID = null, // Required if type is 'thread'
    threadTypeName = '',
    onSuccess
}) => {
    const [value, setValue] = useState('');
    const [loading, setLoading] = useState(false);
    const [deleteAlertOpen, setDeleteAlertOpen] = useState(false);

    // Get Master Data from Redux for validation and ID calculation
    const connExtraFields = useSelector(state => state.masterData.connExtraFields || []);
    const thrExtraFields = useSelector(state => state.masterData.thrExtraFields || []);
    const isMobile = useIsMobile()
    useEffect(() => {
        if (open) {
            setValue(initialValue || '');
        }
    }, [open, initialValue]);

    const getDialogTitle = () => {
        const entity = type === 'connection' ? 'Contact Field' : `${threadTypeName || 'Thread'} Field`;
        return mode === 'edit' ? `Edit ${entity}` : `New ${entity}`;
    };

    const handleSave = async () => {
        if (!value.trim()) {
            toast.error("Please enter a field name.");
            return;
        }

        setLoading(true);
        try {
            let apiKey = type === 'connection' ? 'connExtraFields' : 'thrExtraFields';
            let payload = null;
            let action = mode === 'add' ? 'add' : 'update';

            if (type === 'connection') {
                // --- Connection Logic ---
                // Duplicate Check
                const exists = connExtraFields.some(f => f.F?.toLowerCase() === value?.trim().toLowerCase() && f.FID !== fieldID);
                if (exists) throw new Error("This field name already exists.");

                // Ensure FID is an integer (important for Update API)
                let targetID = fieldID ? parseInt(fieldID) : 0;

                if (mode === 'add') {
                    // Calculate Max ID
                    const maxID = connExtraFields.length > 0 ? Math.max(...connExtraFields.map(f => (f.FID || 0))) : 0;
                    targetID = maxID + 1;
                }

                // Payload for Connection (Add/Update) -> API expects { FID: int, F: string }
                payload = { FID: targetID, F: value.trim() };

            } else {
                // --- Thread Logic ---
                const currentTypeObj = thrExtraFields.find(t => t.TID === threadTypeID);
                const existingFields = currentTypeObj ? JSON.parse(currentTypeObj.Fields).filter(f => !f.IsD) : [];

                // Duplicate Check
                const exists = existingFields.some(f => f.F?.toLowerCase() === value?.trim().toLowerCase() && f.FID !== fieldID);
                if (exists) throw new Error("This field name already exists.");

                let targetID = fieldID;
                if (mode === 'add') {
                    const maxID = existingFields.length > 0 ? Math.max(...existingFields.map(f => f.FID)) : 0;
                    targetID = maxID + 1;
                }

                payload = { TID: threadTypeID, FID: targetID, F: value.trim() };
            }

            await saveMastersConfigAPI(apiKey, action, payload);
            toast.success(`Field ${mode === 'add' ? 'added' : 'updated'}.`);
            if (onSuccess) onSuccess();
            onOpenChange(false);

        } catch (error) {
            toast.error(error.message || "Failed to save field.");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        setLoading(true);
        try {
            let apiKey = type === 'connection' ? 'connExtraFields' : 'thrExtraFields';
            let payload = null;

            if (type === 'connection') {
                payload = value.trim(); // Connection delete API expects the name string usually, or object depending on implementation. 
                // Based on previous code context, simple master delete uses value. 
                // However, saveMastersConfigAPI usually handles objects. Let's send object for consistency if API supports ID.
                // Reverting to previous logic pattern: connection extra fields often deleted by Value or ID. 
                // Assuming standard pattern:
                payload = { FID: fieldID, F: value.trim() };
            } else {
                payload = { TID: threadTypeID, FID: fieldID, F: "" }; // Thread delete marks IsD=true
            }

            await saveMastersConfigAPI(apiKey, 'delete', payload);
            toast.success("Field deleted.");
            setDeleteAlertOpen(false);
            if (onSuccess) onSuccess();
            onOpenChange(false);

        } catch (error) {
            toast.error("Failed to delete field.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="sm:max-w-md w-full">
                    <DialogHeader className="gap-0 px-3 sm:px-4 md:px-4">
                        <div className="sm:hidden flex items-center justify-between w-full h-8">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                                <DialogClose className="p-0 w-auto h-auto flex-shrink-0 flex justify-center">
                                    <ArrowLeft className="h-6 w-6" />
                                </DialogClose>
                                <DialogTitle className="text-lg font-medium">
                                    {getDialogTitle()}
                                </DialogTitle>
                            </div>
                            {mode === 'edit' && (
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => setDeleteAlertOpen(true)}
                                >
                                    <Trash2 className="h-5 w-5" />
                                </Button>
                            )}
                        </div>

                        <DialogTitle className="hidden sm:block text-lg md:text-xl">
                            {getDialogTitle()}
                        </DialogTitle>

                        <div className="hidden sm:flex items-center gap-2">
                            {mode === 'edit' && (
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => setDeleteAlertOpen(true)}
									className="sm:text-text-color sm:hover:bg-gray-200"
                                >
                                    <Trash2 className="h-5 w-5" />
                                </Button>
                            )}
                            <DialogClose className="flex-shrink-0">
                                <X className="h-5 w-5" />
                            </DialogClose>
                        </div>
                    </DialogHeader>

                    <div className="px-4 pb-4 space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="extra-field-name">Field Name</Label>
                            <Input
                                id="extra-field-name"
                                value={value}
                                onChange={(e) => setValue(e.target.value)}
                                placeholder="e.g. Department"
                                className="h-11"
                                autoFocus
                            />
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3  sm:justify-start">
                            <Button onClick={handleSave} disabled={loading} className="sahi-primary-button w-full sm:w-auto">
                                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                                Save Field
                            </Button>
                            {!isMobile && (
                                <Button variant="outline" onClick={() => onOpenChange(false)} className="sahi-secondary-button w-full sm:w-auto">
                                    <X className="h-4 w-4" />
                                    Close
                                </Button>
                            )}
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            <AlertBox
                open={deleteAlertOpen}
                onOpenChange={setDeleteAlertOpen}
                title="Delete Field"
                description={`Are you sure you want to delete "${value}"? This action cannot be undone.`}
                actions={[
                    { label: "Cancel", value: "cancel", variant: "cancel" },
                    { label: "Delete", value: "delete", variant: "action", icon: <Trash2 className="h-4 w-4" /> }
                ]}
                onAction={(val) => val === 'delete' && handleDelete()}
            />
        </>
    );
};

export default ExtraFieldEntryDialog;